import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import { UI_STRINGS } from '@/data/constants/strings'
import { LoadingState } from '@/features/shared-ui'
import { AUTH_ROUTES } from '@/routes/routeConstants'
import { useAuth } from '../hooks/useAuth'
import { AccessGateMessage } from './AccessGateMessage'

/**
 * Blocks the app until the permission profile is known. Users with no
 * accessible customers see a "contact your administrator" notice instead of
 * empty dashboards; everyone else proceeds and the backend scopes what they
 * can see.
 */
export function AccessGate({ children }) {
  const { pathname } = useLocation()
  const {
    user,
    loading,
    signOut,
    profile,
    profileLoading,
    profileError,
    refetchProfile,
    hasAnyAccess,
  } = useAuth()

  const logOut = { label: 'Log Out', onClick: () => signOut() }

  // Login page is always reachable; unauthenticated users fall through to
  // ProtectedRoute, which redirects there
  if (pathname === AUTH_ROUTES.LOGIN || (!loading && !user)) {
    return children
  }

  if (loading || profileLoading) {
    return <LoadingState message='Checking access...' />
  }

  if (!profile) {
    return (
      <AccessGateMessage
        severity='error'
        title="Couldn't load your permissions"
        message={profileError?.message || 'Please try again in a moment.'}
        actions={[
          { label: 'Retry', onClick: () => refetchProfile(), variant: 'contained' },
          logOut,
        ]}
      />
    )
  }

  if (!hasAnyAccess) {
    return (
      <AccessGateMessage
        severity='warning'
        title='No access yet'
        message={UI_STRINGS.NO_ACCESS_ERROR}
        actions={[
          { label: 'Check again', onClick: () => refetchProfile(), variant: 'contained' },
          logOut,
        ]}
      />
    )
  }

  return children
}

AccessGate.propTypes = {
  children: PropTypes.node.isRequired,
}
